import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import Select from 'react-select';
import {
  FaTrash,
  FaTools,
  FaPlus,
  FaUserTie,
  FaHardHat,
  FaLink,
  FaSearch
} from 'react-icons/fa';

const AffectationsPage = ({ user }) => {
  const [affectations, setAffectations] = useState([]);
  const [users, setUsers] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    user_id: '',
    chantier_id: ''
  });

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAll();
    }
  }, [user]);

  const fetchAll = async () => {
    try {
      setLoading(true);

      const [resAffectations, resUsers, resChantiers] = await Promise.all([
        axios.get('/user_chantiers'),
        axios.get('/users'),
        axios.get('/chantiers')
      ]);

      setAffectations(Array.isArray(resAffectations.data) ? resAffectations.data : []);
      setUsers(Array.isArray(resUsers.data) ? resUsers.data : []);
      setChantiers(Array.isArray(resChantiers.data) ? resChantiers.data : []);
    } catch (err) {
      setPageError("❌ Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      chantier_id: ''
    });
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setModalError(null);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!formData.user_id || !formData.chantier_id) {
      setModalError("⚠️ Veuillez sélectionner un utilisateur et un chantier.");
      return;
    }

    try {
      setSaving(true);
      await axios.post('/user_chantiers', formData);
      setShowModal(false);
      resetForm();
      fetchAll();
    } catch (err) {
      setModalError(err.response?.data?.message || "❌ Erreur d'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("🗑️ Supprimer cette affectation ?")) {
      try {
        await axios.delete(`/user_chantiers/${id}`);
        fetchAll();
      } catch (err) {
        setPageError("❌ Erreur lors de la suppression.");
      }
    }
  };

  const userOptions = useMemo(() => {
    return users.map((u) => ({
      value: u.id,
      label: `${u.name || 'Sans nom'} (${u.login || 'Sans login'})`,
      raw: u
    }));
  }, [users]);

  const chantierOptions = useMemo(() => {
    return chantiers.map((c) => ({
      value: c.id,
      label: c.name || `Chantier #${c.id}`,
      raw: c
    }));
  }, [chantiers]);

  const selectedUserOption = userOptions.find(
    (option) => String(option.value) === String(formData.user_id)
  ) || null;

  const selectedChantierOption = chantierOptions.find(
    (option) => String(option.value) === String(formData.chantier_id)
  ) || null;

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      borderRadius: '10px',
      borderColor: state.isFocused ? '#8b5e3c' : '#d7c3a7',
      boxShadow: state.isFocused ? '0 0 0 0.15rem rgba(139, 94, 60, 0.15)' : 'none',
      '&:hover': {
        borderColor: '#8b5e3c'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#6d4e36'
        : state.isFocused
        ? '#f5f5db'
        : '#fff',
      color: state.isSelected ? '#fff' : '#6d4e36',
      cursor: 'pointer'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#8b6f58'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#6d4e36'
    })
  };

  if (!user || user.role !== 'admin') {
    return (
      <Alert variant="danger" className="m-4 shadow-sm">
        ⛔ Accès réservé aux administrateurs
      </Alert>
    );
  }

  const columns = [
    {
      dataField: 'user_name',
      text: '👤 Nom',
      sort: true,
      filter: textFilter({
        placeholder: '🔍 Nom...',
        className: 'form-control form-control-sm'
      }),
      headerStyle: {
        backgroundColor: '#f5f5db',
        color: '#6d4e36'
      }
    },
    {
      dataField: 'user_login',
      text: '🔐 Login',
      sort: true,
      filter: textFilter({
        placeholder: '🔍 Login...',
        className: 'form-control form-control-sm'
      }),
      headerStyle: {
        backgroundColor: '#f5f5db',
        color: '#6d4e36'
      }
    },
    {
      dataField: 'user_role',
      text: '🛡️ Rôle',
      sort: true,
      formatter: (cell) => {
        const role = String(cell || '').toLowerCase();

        let bg = 'secondary';
        if (role === 'admin') bg = 'danger';
        if (role === 'responsable') bg = 'warning';
        if (role === 'pointeur') bg = 'info';

        return <Badge bg={bg}>{cell || '-'}</Badge>;
      },
      filter: textFilter({
        placeholder: '🔍 Rôle...',
        className: 'form-control form-control-sm'
      }),
      headerStyle: {
        backgroundColor: '#f5f5db',
        color: '#6d4e36'
      }
    },
    {
      dataField: 'chantier_name',
      text: '🏗️ Chantier',
      sort: true,
      filter: textFilter({
        placeholder: '🔍 Chantier...',
        className: 'form-control form-control-sm'
      }),
      headerStyle: {
        backgroundColor: '#f5f5db',
        color: '#6d4e36'
      }
    },
    {
      dataField: 'actions',
      text: (
        <>
          <FaTools className="me-1" />
          Actions
        </>
      ),
      formatter: (_, row) => (
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleDelete(row.id)}
          title="Supprimer l'affectation"
        >
          <FaTrash />
        </Button>
      ),
      headerAlign: 'center',
      align: 'center',
      headerStyle: {
        width: '110px',
        backgroundColor: '#f5f5db',
        color: '#6d4e36'
      }
    }
  ];

  return (
    <div className="container mt-4">
      <Card
        className="shadow-sm border-0"
        style={{
          borderRadius: '18px',
          overflow: 'hidden'
        }}
      >
        <Card.Header
          className="d-flex justify-content-between align-items-center flex-wrap gap-2"
          style={{
            background: 'linear-gradient(135deg, #6d4e36 0%, #8b5e3c 100%)',
            color: '#fff',
            padding: '1rem 1.25rem'
          }}
        >
          <div>
            <h3 className="mb-1 d-flex align-items-center">
              <FaLink className="me-2" />
              Affectations Utilisateurs ↔ Chantiers
            </h3>
            <small style={{ opacity: 0.9 }}>
              Gestion des accès chantier par utilisateur
            </small>
          </div>

          <Button
            variant="light"
            onClick={openModal}
            className="fw-bold"
            style={{ borderRadius: '10px' }}
          >
            <FaPlus className="me-2" />
            Ajouter
          </Button>
        </Card.Header>

        <Card.Body style={{ backgroundColor: '#fcfbf7' }}>
          {pageError && (
            <Alert variant="danger" dismissible onClose={() => setPageError(null)} className="mb-3">
              {pageError}
            </Alert>
          )}
          <Row className="mb-3">
            <Col md={4} className="mb-2">
              <Card
                className="border-0 shadow-sm h-100"
                style={{ borderRadius: '14px', backgroundColor: '#f5f5db' }}
              >
                <Card.Body className="py-3">
                  <div className="d-flex align-items-center">
                    <FaLink className="me-2" style={{ color: '#6d4e36' }} />
                    <div>
                      <div className="fw-bold" style={{ color: '#6d4e36' }}>
                        Total affectations
                      </div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#8b5e3c' }}>
                        {affectations.length}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4} className="mb-2">
              <Card
                className="border-0 shadow-sm h-100"
                style={{ borderRadius: '14px', backgroundColor: '#f5f5db' }}
              >
                <Card.Body className="py-3">
                  <div className="d-flex align-items-center">
                    <FaUserTie className="me-2" style={{ color: '#6d4e36' }} />
                    <div>
                      <div className="fw-bold" style={{ color: '#6d4e36' }}>
                        Utilisateurs chargés
                      </div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#8b5e3c' }}>
                        {users.length}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4} className="mb-2">
              <Card
                className="border-0 shadow-sm h-100"
                style={{ borderRadius: '14px', backgroundColor: '#f5f5db' }}
              >
                <Card.Body className="py-3">
                  <div className="d-flex align-items-center">
                    <FaHardHat className="me-2" style={{ color: '#6d4e36' }} />
                    <div>
                      <div className="fw-bold" style={{ color: '#6d4e36' }}>
                        Chantiers chargés
                      </div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#8b5e3c' }}>
                        {chantiers.length}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {loading ? (
            <Alert variant="light" className="shadow-sm">
              ⏳ Chargement des affectations...
            </Alert>
          ) : (
            <div
              className="shadow-sm"
              style={{
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                padding: '10px'
              }}
            >
              <BootstrapTable
                keyField="id"
                data={affectations}
                columns={columns}
                pagination={paginationFactory({
                  sizePerPage: 10,
                  sizePerPageList: [
                    { text: '10', value: 10 },
                    { text: '25', value: 25 },
                    { text: '50', value: 50 }
                  ],
                  showTotal: true,
                  paginationTotalRenderer: (from, to, size) => (
                    <span className="ms-2 text-muted">
                      Affichage de {from} à {to} sur {size} élément(s)
                    </span>
                  )
                })}
                filter={filterFactory()}
                striped
                hover
                condensed
                bordered={false}
                rowStyle={() => ({
                  backgroundColor: '#fff',
                  color: '#4e3929'
                })}
                noDataIndication="Aucune affectation trouvée ❌"
              />
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} centered backdrop="static">
        <Modal.Header
          closeButton={!saving}
          style={{
            backgroundColor: '#f5f5db',
            borderBottom: '1px solid #e5d7c4'
          }}
        >
          <Modal.Title style={{ color: '#6d4e36' }}>
            <FaPlus className="me-2" />
            Nouvelle Affectation
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: '#fcfbf7' }}>
          {modalError && (
            <Alert variant="warning" className="mb-3">
              {modalError}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold" style={{ color: '#6d4e36' }}>
                <FaUserTie className="me-2" />
                Utilisateur
              </Form.Label>
              <Select
                options={userOptions}
                value={selectedUserOption}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    user_id: selected ? selected.value : ''
                  }))
                }
                placeholder="🔎 Rechercher un utilisateur..."
                isClearable
                styles={customSelectStyles}
                noOptionsMessage={() => 'Aucun utilisateur trouvé'}
              />
              <Form.Text className="text-muted">
                <FaSearch className="me-1" />
                Tu peux écrire le nom ou le login pour filtrer.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="fw-bold" style={{ color: '#6d4e36' }}>
                <FaHardHat className="me-2" />
                Chantier
              </Form.Label>
              <Select
                options={chantierOptions}
                value={selectedChantierOption}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    chantier_id: selected ? selected.value : ''
                  }))
                }
                placeholder="🔎 Rechercher un chantier..."
                isClearable
                styles={customSelectStyles}
                noOptionsMessage={() => 'Aucun chantier trouvé'}
              />
              <Form.Text className="text-muted">
                <FaSearch className="me-1" />
                Tu peux écrire une partie du nom du chantier.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: '#fcfbf7', borderTop: '1px solid #eee' }}>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              backgroundColor: '#6d4e36',
              borderColor: '#6d4e36'
            }}
          >
            <FaPlus className="me-1" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AffectationsPage;